import { API_BASE_URL, authFetch } from '../../config/api';

const baseUrl = API_BASE_URL;

export interface LastRun {
  run_time: string;
  script_name: string;
  status: string;
}

export interface ScriptDef {
  name: string;
  label: string;
  description: string;
}

export interface ScriptRun {
  id: number;
  script_name: string;
  status: string;
  output: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export async function fetchTestDb(): Promise<{ ok: boolean }> {
  const res = await authFetch(`${baseUrl}/api/test-db`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  return (await res.json()) as { ok: boolean };
}

export async function fetchTestPrisma(): Promise<{ ok: boolean }> {
  const res = await authFetch(`${baseUrl}/api/test-prisma`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  return (await res.json()) as { ok: boolean };
}

export async function fetchLastUpdated(): Promise<{ lastRuns: LastRun[] }> {
  const res = await authFetch(`${baseUrl}/api/last-updated`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  return (await res.json()) as { lastRuns: LastRun[] };
}

export interface SourceFreshness {
  source: string;
  newest_published: string | null;
  days_since: number | null;
  event_days: number;
  median_gap_days: number | null;
  p90_gap_days: number | null;
  threshold_days: number;
  is_stale: boolean;
  informational?: boolean;
  // Chain parked in the pipeline (data_ingestion/pipeline_parked.py): not run
  // nightly, never alerted; note says why and when it was last attempted.
  parked?: boolean;
  note?: string | null;
  checked_at: string;
}

// Open alert episode of the nightly curated_schema rebuild; null while it is clean.
export interface GateAlert {
  status: 'failure' | 'warning';
  since: string;
  last_alerted_at: string | null;
}

export interface SourceFreshnessResponse {
  sources: SourceFreshness[];
  checkedAt: string | null;
  gate?: GateAlert | null;
}

export async function fetchSourceFreshness(): Promise<SourceFreshnessResponse> {
  const res = await authFetch(`${baseUrl}/api/source-freshness`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  return (await res.json()) as SourceFreshnessResponse;
}

// The host's Claude Code CLI behind the gateway (GET /api/claude-status).
export interface ClaudeStatus {
  ok: boolean;
  error?: string;
  version?: string | null;
  auth?: { loggedIn?: boolean; subscriptionType?: string; authMethod?: string; error?: string };
  busy?: boolean;
  lock?: { owner?: string; label?: string } | null;
  models: { haiku: string; opus: string };
  effort: string;
}

export async function fetchClaudeStatus(): Promise<ClaudeStatus> {
  const res = await authFetch(`${baseUrl}/api/claude-status`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  return (await res.json()) as ClaudeStatus;
}

export async function fetchScripts(): Promise<ScriptDef[]> {
  const res = await authFetch(`${baseUrl}/api/knowledge/scripts`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  const data = await res.json();
  return data.scripts;
}

export async function fetchScriptRuns(): Promise<ScriptRun[]> {
  const res = await authFetch(`${baseUrl}/api/knowledge/scripts/runs`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  const data = await res.json();
  return data.runs;
}

export async function triggerScript(script_name: string): Promise<{ run_id: number }> {
  const res = await authFetch(`${baseUrl}/api/knowledge/scripts/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ script_name }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function isActiveStatus(status: string): boolean {
  return status === 'pending' || status === 'claimed' || status === 'running';
}
